import PlusIcon from '@components/icons/PlusIcon';
import SearchIcon from '@components/icons/SearchIcon';
import { useMediaQuery } from '@material-ui/core';
import CheckIcon from '@mui/icons-material/Check';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import VideoInstruction from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/VideoInstruction';
import CargoSpecialConditional from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/сargoSpecialConditional';
import {
  CustomCargoParameters,
  CustomCargoRouteCalculationParameters,
  RouteWidgetBaseParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import useDelivery from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import { cargoConverterService } from '@services/requests/routeCalculator/cargoConverterService';
import React, { FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { checkEmptyInputValue } from '../../../helpers';
import { CalculationsControlsStyled } from '../CalculationsMenu/StyledComponents';
import { Favorites } from '../Favorites';
import { History } from '../History';
import { TransportTypesParameters } from '../TransportTypesParameters';
import CustomCargoParametersItem from './CustomCargoParametersItem';

// Свойства компонента
export interface CustomCargoParametersComponentProps {
  BaseData: RouteWidgetBaseParameters;
  customCargoParameters: CustomCargoRouteCalculationParameters;
  mainLanguage: string; // Текущий основной язык
  systemLanguage: string; // Системный язык
  handleDeleteCustomParameters(index: number): void;
  baseConditionsSlot?: React.ReactNode;
  resetSlot?: React.ReactNode;
  onChangeCargoFieldsFilled: { (validityResults: boolean[]): void };
  copyNotificationVisible: boolean;
}

// Компонент вывода конфигурации сборных грузов для расчета параметров
const CustomCargoParametersComponent: FC<CustomCargoParametersComponentProps> = (props) => {
  const {
    baseConditionsSlot,
    resetSlot,
    copyNotificationVisible,
    handleDeleteCustomParameters,
    customCargoParameters: { customCargoParameters: cargoParameters, isDangerousCargo },
    onChangeCargoFieldsFilled,
    BaseData,
  } = props;
  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const [routeParameter, setRouteParameter] = useRouteParams().useState();
  const { t } = useTranslation('Actions');
  const theme = useTheme();
  const isNotMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const [isSelectionLoading, setIsSelectionLoading] = useState<boolean>(false);
  const [isSelectingError, setIsSelectingError] = useState<boolean>(false);

  const isCargoFieldsFilled = useMemo(
    () =>
      cargoParameters
        .map((parameters) => {
          switch (parameters.parametersType) {
            case 'base':
              return [
                checkEmptyInputValue(parameters.baseParameters.height),
                checkEmptyInputValue(parameters.baseParameters.length),
                checkEmptyInputValue(parameters.baseParameters.quantity),
                checkEmptyInputValue(parameters.baseParameters.weight),
                checkEmptyInputValue(parameters.baseParameters.width),
              ];
            case 'pallet':
              const pallet = BaseData.units.find((u) => u.id === parameters.palletParameters.type);
              return [
                checkEmptyInputValue(parameters.palletParameters.normativeHeightOfStacking),
                checkEmptyInputValue(parameters.palletParameters.weight),
                checkEmptyInputValue(parameters.palletParameters.quantity),
                !pallet,
              ];
            case 'volume':
              return [
                checkEmptyInputValue(parameters.volumeParameters.volume),
                checkEmptyInputValue(parameters.volumeParameters.weight),
              ];
            default:
              return [];
          }
        })
        .flat()
        .every((value) => !value),
    [cargoParameters]
  );

  /**
   * Обработка изменения одного из параметров сборного груза
   * @param customCargoParameters
   * @param index
   */
  const handleChangeCustomCargoParameters = (
    customCargoParameters: CustomCargoParameters,
    index: number
  ) => {
    setRouteParameter((current) => {
      // Создание копии массива customCargoParameters
      const updatedCustomCargos = [...current.customCargoParameters.customCargoParameters];

      // Обновление элемента в скопированном массиве
      updatedCustomCargos[index] = customCargoParameters;

      // Возвращение обновленного состояния
      return {
        ...current,
        customCargoParameters: {
          ...current.customCargoParameters,
          customCargoParameters: updatedCustomCargos,
          transportTypes: [],
        },
      };
    });
  };

  /**
   * Обработка добавления нового контейнера в общий пулл
   */
  const handleAddNewContainer = () => {
    const defaultPalletType = props.BaseData.units.filter((u) => u.unit_group === '4')[0].id || '0';
    setRouteParameter((current) => ({
      ...current,
      customCargoParameters: {
        ...current.customCargoParameters,
        transportTypes: [],
        customCargoParameters: [
          ...current.customCargoParameters.customCargoParameters,
          {
            parametersType: 'base',
            parametersCargoCalcType: 'volume',
            baseParameters: {
              quantity: 1,
              length: '1',
              width: '1',
              height: '1',
              weight: '10',
            },
            palletParameters: {
              type: defaultPalletType,
              quantity: 1,
              normativeHeightOfStacking: '1',
              weight: 1000,
            },
            volumeParameters: {
              volume: '1',
              weight: '10',
              height: 1,
            },
          },
        ],
      },
    }));
  };

  const selectCargoParametersHandler = async () => {
    if (isRoutesLoading || isLoading || !isCargoFieldsFilled) {
      return;
    }

    setIsSelectionLoading(true);
    const selectCargoData = await cargoConverterService().GetSelectedParams(
      cargoParameters.map((cargo) => ({ ...cargo, customCargoType: cargo.parametersType })),
      routeParameter.cargoType
    );

    if (!selectCargoData || (!selectCargoData.conversationCargos && !!selectCargoData.errorMsg)) {
      setIsSelectingError(true);
      setIsSelectionLoading(false);
      return;
    }
    setIsSelectionLoading(false);

    const selectedMultiParam = selectCargoData.conversationCargos.multiParam[0];
    setRouteParameter((prevState) => ({
      ...prevState,
      customCargoParameters: {
        ...prevState.customCargoParameters,
        cargoFingerprint: selectedMultiParam.cargoFingerprint,
        transportTypes: [
          ...selectedMultiParam.containerLoads.map((c) => ({
            weight: Math.round(c.usedWeight),
            transportTypeId: c.transportTypeId,
            type: 'container' as const,
          })),
          ...selectedMultiParam.truckLoads.map((t) => ({
            weight: Math.round(t.usedWeight),
            transportTypeId: t.transportTypeId,
            type: 'truck' as const,
          })),
        ],
        customCargoParameters: selectCargoData.customCargoParameters.map((c) => ({
          ...c,
          parametersType: c.customCargoType,
          parametersCargoCalcType: c.customCargoType === 'volume' ? 'volume' : 'base',
          baseParameters: {
            quantity: c.baseParameters.quantity,
            length: c.baseParameters.length.toString(),
            width: c.baseParameters.width.toString(),
            height: c.baseParameters.height.toString(),
            weight: c.baseParameters.weight.toString(),
          },
          palletParameters: {
            type: c.palletParameters.type,
            quantity: c.palletParameters.quantity,
            normativeHeightOfStacking: c.palletParameters.normativeHeightOfStacking.toString(),
            weight: c.palletParameters.weight,
          },
          volumeParameters: {
            volume: c.volumeParameters.volume.toString(),
            weight: c.volumeParameters.weight.toString(),
            height: 1,
          },
        })),
      },
    }));
  };

  return (
    <>
      {routeParameter.cargoType === 'fclFtl' && (
        <div>
          <Alert color={'info'} severity={'info'}>
            <p>
              <Trans
                i18nKey={'CargoSelection.SelectionDialog.InfoTextFclFtl'}
                components={[
                  <b key="bold" />,
                  <br key="br" />,
                  <SearchIcon
                    key="search"
                    sx={{
                      fontSize: 18,
                      color: '#0EAB70',
                    }}
                  />,
                ]}
              />
            </p>
          </Alert>
        </div>
      )}
      <section className="cargo-list">
        {cargoParameters.map((cargo, i) => {
          const type: 'base' | 'volume' | 'pallet' =
            cargo.parametersCargoCalcType === 'base'
              ? cargo.parametersType
              : cargo.parametersCargoCalcType;
          return (
            <div key={`list--item-${i}`} className={'cargo-item'}>
              <CustomCargoParametersItem
                Position={i + 1}
                BaseData={props.BaseData}
                Type={type}
                customCargoParameters={cargo}
                mainLanguage={props.mainLanguage}
                systemLanguage={props.systemLanguage}
                isDeletable={cargoParameters.length > 1}
                onChangeCustomCargoParameters={(params) =>
                  handleChangeCustomCargoParameters(params, i)
                }
                onDelete={() => handleDeleteCustomParameters(i)}
                onChangeCargoFieldsFilled={onChangeCargoFieldsFilled}
              />
            </div>
          );
        })}
        <IconButton
          title={`+ ${t('Actions:Add')}`}
          className={'add-new-cargo'}
          disabled={isRoutesLoading || isLoading}
          onClick={handleAddNewContainer}
        >
          <PlusIcon />
        </IconButton>
      </section>
      {routeParameter.cargoType === 'fclFtl' && (
        <>
          <Tooltip title={t('Common:CargoSelection.SelectionDialog.RunSelectionButtonHint')}>
            <Button
              onClick={selectCargoParametersHandler}
              className={'pallet-edit-button'}
              variant={'contained'}
              color={'primary'}
              fullWidth={true}
              size="medium"
              disabled={isRoutesLoading || isLoading || !isCargoFieldsFilled}
            >
              {t('Common:CargoSelection.SelectionDialog.RunSelectionButton')}
            </Button>
          </Tooltip>
          {isSelectingError && !isSelectionLoading && (
            <span className="selection-error">
              {t('Common:CargoSelection.SelectionDialog.SelectionError')}
            </span>
          )}
          <TransportTypesParameters
            BaseData={props.BaseData}
            mainLanguage={props.mainLanguage}
            transportTypes={routeParameter.customCargoParameters.transportTypes}
            isSelectingError={isSelectingError}
            isSelectionLoading={isSelectionLoading}
          />
        </>
      )}

      <div className="footer">
        <div className="calculator-options-wrapper">
          <div className={'base-params'}>{baseConditionsSlot}</div>
          <CargoSpecialConditional isDangerousCargo={isDangerousCargo} />
        </div>
        <CalculationsControlsStyled>
          <div className={'control'}>
            {isNotMobile && (
              <>
                <VideoInstruction />
                <Favorites />
                <History />
              </>
            )}
            {resetSlot && (
              <div className={'routes-calculation-widget__footer-reset-container'}>
                <>
                  {copyNotificationVisible && (
                    <Alert
                      icon={<CheckIcon fontSize="inherit" />}
                      severity="success"
                      className="copy-success-alert"
                    >
                      {t('RoutesCalculationWidget:CopyCalculationLinkSuccess')}
                    </Alert>
                  )}
                  {resetSlot}
                </>
              </div>
            )}
          </div>
        </CalculationsControlsStyled>
      </div>
    </>
  );
};

// Экспортируем компонент
export default CustomCargoParametersComponent;
